import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

// ─── Token helpers ────────────────────────────────────────────────────────────
// Store the JWT in localStorage so it survives page reloads and works across
// cross-origin deployments (avoids SameSite/third-party cookie issues).

export const TOKEN_KEY = 'st_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// ─── Axios instance ───────────────────────────────────────────────────────────

const api = axios.create({
  baseURL: API,
  withCredentials: true,               // still send cookies as fallback
  headers: { 'Content-Type': 'application/json' },
  // No global timeout — user-initiated requests (login, save, upload) must never
  // be killed mid-flight. The only place we want a timeout is the background
  // /auth/me check; that is handled per-request in AuthContext.js.
});

// Attach token to every request if present
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// On 401, clear token and notify app — but ONLY for non-auth endpoints
// (login/register/verify-2fa return 401 for bad credentials, not session expiry)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err.config?.url || '';
    const isAuthEndpoint = url.includes('/auth/login') ||
                           url.includes('/auth/register') ||
                           url.includes('/auth/verify-2fa') ||
                           url.includes('/auth/forgot-password');
    if (err.response?.status === 401 && !isAuthEndpoint) {
      // A 401 after the browser followed a redirect to another origin dropped
      // the Authorization header on the way. That is not a logged-out session.
      let redirectedOffOrigin = false;
      try {
        if (err.request && err.request.responseURL) {
          redirectedOffOrigin = new URL(err.request.responseURL).origin !== window.location.origin;
        }
      } catch {
        redirectedOffOrigin = false;
      }
      if (!redirectedOffOrigin) {
        clearToken();
        window.dispatchEvent(new Event('st:unauthorized'));
      }
    }
    // SEC-C3: 403 with error_code "owner_inactive" means the project owner's
    // subscription lapsed. Show the inactive notice, never the paywall.
    if (err.response?.status === 403) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail?.error_code === 'owner_inactive') {
        window.dispatchEvent(new CustomEvent('st:owner-inactive', {
          detail: { message: detail.message || 'This project is temporarily unavailable. Please contact your contractor.' },
        }));
        return Promise.reject(err);
      }
    }
    // 402 = subscription required: fire paywall event.
    // R3 PR B: 402 archive_paywall is a client archive gate, handled by
    // ProjectPage (ArchivePaywall), never the subscription paywall.
    if (err.response?.status === 402) {
      const detail = err.response?.data?.detail || '';
      const isArchive = typeof detail === 'object' && detail?.error_code === 'archive_paywall';
      const setupRequired = typeof detail === 'object' && detail?.error_code === 'setup_required';
      if (setupRequired) {
        if (window.location.pathname !== '/finish-setup') {
          window.location.assign('/finish-setup');
        }
      } else if (!isArchive) {
        const reason = typeof detail === 'string' ? detail : (detail?.message || '');
        window.dispatchEvent(new CustomEvent('st:paywall', { detail: { reason } }));
      }
    }
    return Promise.reject(err);
  }
);

export default api;
export { BACKEND_URL };
